/**
 * Purchase Order Type Definitions.
 */

export interface LineCharge {
  id: string;
  line_id: string;
  charge_type: string;
  description: string;
  amount: number;
  /** When set, this charge was auto-applied by a fee scheme. */
  tax_scheme_id: string | null;
  created_at: string;
}

export interface PurchaseOrderLine {
  id: string;
  purchase_order_id: string;
  asset_id: string | null;
  description: string;
  asset_name: string | null;
  year: number | null;
  make: string | null;
  model: string | null;
  variant: string | null;
  serial_number: string | null;
  vin: string | null;
  lot_number: string | null;
  category: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  line_fees: number;
  line_fee_description: string | null;
  line_total: number;
  line_status: string;
  received_quantity: number;
  received_at: string | null;
  received_by: string | null;
  expected_delivery_date: string | null;
  tracking_number: string | null;
  notes: string | null;
  charges: LineCharge[];
  created_at: string;
}

export interface PurchaseOrder {
  id: string;
  organization_id: string;
  po_number: string;
  vendor_id: string | null;
  vendor_name: string | null;
  location_id: string | null;
  status: string;
  order_date: string | null;
  expected_delivery_date: string | null;
  received_date: string | null;
  currency: string;
  exchange_rate: number | null;
  subtotal: number;
  shipping_cost: number;
  tax: number;
  /** Sum of all line-level fees/charges (line_fees + line charges); server-computed so the totals block reconciles. */
  fees_total: number;
  total: number;
  funding_account_id: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  line_count?: number;
  line_items: PurchaseOrderLine[];
  tax_lines: import("./sales-order").OrderTaxLine[];
}

export interface PurchaseOrderCreate {
  vendor_id?: string;
  order_date?: string;
  expected_delivery_date?: string;
  shipping_cost?: number;
  tax?: number;
  funding_account_id?: string;
  notes?: string;
}

export interface POLineCreate {
  description: string;
  asset_name?: string;
  year?: number;
  make?: string;
  model?: string;
  variant?: string;
  serial_number?: string;
  vin?: string;
  lot_number?: string;
  category?: string;
  quantity?: number;
  unit_price: number;
  custom_fields?: Record<string, string>;
}

export interface VendorContact {
  id: string;
  vendor_id: string;
  name: string;
  title: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  is_primary: boolean;
  notes: string | null;
  created_at: string;
}

export interface Vendor {
  id: string;
  organization_id: string;
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  address: Record<string, string> | null;
  default_currency: string | null;
  website: string | null;
  tax_id: string | null;
  tax_tags: string[] | null;
  tax_exempt: boolean;
  default_tax_state: string | null;
  payment_terms: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  contacts: VendorContact[];
}

export interface VendorCreate {
  name: string;
  contact_name?: string;
  email?: string;
  phone?: string;
  address?: Record<string, string>;
  website?: string;
  tax_id?: string;
  tax_tags?: string[];
  tax_exempt?: boolean;
  default_tax_state?: string;
  payment_terms?: string;
  default_currency?: string;
  notes?: string;
}

export const PO_STATUSES = [
  "Draft",
  "Submitted",
  "In-Transit",
  "Partially Received",
  "Received",
  "Cancelled",
] as const;
